import * as Select from '@radix-ui/react-select';
import { useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Scrollbars } from '~/chords/Scrollbars';
import { DropdownItemClear } from '~/components/DropdownTreeSingleSelect/components/DropdownItemClear';
import { DropdownItems } from '~/components/DropdownTreeSingleSelect/components/DropdownItems';
import { DropdownNoOptionsAvailable } from '~/components/DropdownTreeSingleSelect/components/DropdownNoOptionsAvailable';
import { DropdownSearch } from '~/components/DropdownTreeSingleSelect/components/DropdownSearch';
import type {
  DropdownClearOptionsInternal,
  DropdownContentOptions,
  DropdownSearchOptions,
  InternalDropdownOption,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/components/DropdownTreeSingleSelect/types';
import { cn } from '~/lib/cn';

interface DropdownMenuProps extends RadixUiProps {
  options: InternalDropdownOption[];
  contentOptions: DropdownContentOptions;
  searchOptions?: DropdownSearchOptions;
  clearOptions: DropdownClearOptionsInternal;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  selectedOption?: InternalDropdownOption;
  onSelectOption: (option: InternalDropdownOption) => void;
  onExpandOption: (option: InternalDropdownOption, isExpanded: boolean) => void;
  showCheckIcon?: boolean;
  OptionTemplate?: OptionTemplateComponent;
}

export function DropdownMenu({
  options,
  contentOptions,
  searchOptions,
  clearOptions,
  searchQuery,
  onSearchQueryChange,
  selectedOption,
  onSelectOption,
  onExpandOption,
  showCheckIcon,
  OptionTemplate,
  radix = {},
}: DropdownMenuProps) {
  const { t } = useTranslation('common');
  const isOneOrMoreOptionsVisible = options.some((option) => option.isVisible);
  const searchQueryTrimmed = searchQuery.trim();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    return () => {
      window.clearTimeout(timeoutRef.current);
    };
  }, []);

  /**
   * When search is enabled and the input has a query, Escape clears the search
   * and refocuses the input instead of closing the dropdown. When the search
   * input is empty or search is disabled, Escape closes the dropdown as normal.
   */
  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    // If the search input field is empty or not enabled, allow the dropdown to close as normal.
    if (!searchOptions?.isEnabled || !searchQueryTrimmed) {
      return;
    }

    // If the search input field is populated and the user presses the Escape key,
    // clear the search input and focus the cursor into it.
    event.preventDefault();

    onSearchQueryChange('');

    timeoutRef.current = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 10);
  };

  const showClearOptionTop =
    clearOptions?.isEnabled &&
    (clearOptions.placements.includes('option-top') ||
      clearOptions.placements.includes('default')) &&
    selectedOption &&
    !searchQueryTrimmed;

  const radixWithKeyboardNav: typeof radix = {
    ...radix,
    selectGroup: {
      ...radix.selectGroup,
      onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
        // Navigate to the search input when the user presses ArrowUp on the first option.
        const targetElement = event.target as HTMLElement;
        const isFirstOption = showClearOptionTop
          ? targetElement?.dataset?.clearSelectItem !== undefined
          : targetElement?.dataset?.itemDepth === '0' &&
            event.currentTarget.firstChild?.contains(targetElement);

        if (event.key === 'ArrowUp' && isFirstOption) {
          searchInputRef.current?.focus();
        }
      },
    },
  };

  const ClearOption = clearOptions.OptionTemplate ?? DropdownItemClear;

  return (
    <Select.Portal>
      <Select.Content
        // Prevents the dropdown menu from overlapping the dropdown trigger.
        // https://www.radix-ui.com/primitives/docs/components/select#content
        position="popper"
        {...radixWithKeyboardNav.selectContent}
        className={cn(
          'mt-xs min-w-[var(--radix-popper-anchor-width)] max-w-[var(--radix-popper-available-width)] overflow-auto text-body-sm text-default',
          'rounded-xs border shadow-xl drop-shadow-xl bg-level-03 border-emphasis-strong',
          radixWithKeyboardNav.selectContent?.className,
        )}
        style={
          {
            // This is used to calculate the scroll height of the dropdown menu.
            '--dropdown-menu-search-height': searchOptions?.isEnabled
              ? '72px'
              : '0px',
            '--dropdown-menu-clear-option-height': showClearOptionTop
              ? '36px'
              : '0px',
            '--dropdown-menu-max-height': contentOptions.maxHeight?.trim()
              ? contentOptions.maxHeight.trim()
              : '600px',
            zIndex: contentOptions.zIndex,
          } as React.CSSProperties
        }
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        {!searchQuery && !options?.length ? (
          <DropdownNoOptionsAvailable radix={radixWithKeyboardNav} />
        ) : (
          <>
            {searchOptions?.isEnabled ? (
              <DropdownSearch
                value={searchQuery}
                onChange={onSearchQueryChange}
                inputRef={searchInputRef}
                maxLength={searchOptions.maxLength}
              />
            ) : null}

            {showClearOptionTop ? (
              <ClearOption radix={radixWithKeyboardNav} />
            ) : null}

            <Select.Viewport {...radixWithKeyboardNav.selectViewport}>
              <Scrollbars asChild>
                <div className="max-h-dropdown-tree-single-select-scroll">
                  {isOneOrMoreOptionsVisible ? (
                    <DropdownItems
                      depth={0}
                      options={options}
                      searchQuery={searchQueryTrimmed}
                      selectedOption={selectedOption}
                      onSelectOption={onSelectOption}
                      onExpandOption={onExpandOption}
                      showCheckIcon={showCheckIcon}
                      OptionTemplate={OptionTemplate}
                      radix={radixWithKeyboardNav}
                    />
                  ) : (
                    <DropdownNoOptionsAvailable
                      labelText={t('dropdownTree.noMatchingOptionsText', {
                        searchQuery: searchQueryTrimmed,
                      })}
                      labelNode={
                        <span className="text-emphasis-stronger">
                          <Trans
                            i18nKey="dropdownTree.noMatchingOptionsHtml"
                            t={t}
                            values={{
                              searchQuery: searchQueryTrimmed,
                            }}
                            components={{
                              strong: (
                                <span className="font-semibold text-default" />
                              ),
                            }}
                          />
                        </span>
                      }
                      radix={radixWithKeyboardNav}
                    />
                  )}
                </div>
              </Scrollbars>
            </Select.Viewport>
          </>
        )}
      </Select.Content>
    </Select.Portal>
  );
}

DropdownMenu.displayName = 'DropdownMenu';
